/**
 * Image carousel custom element with infinite loop, horizontal swipe, autoplay, and dot navigation.
 * Uses clone-and-reset technique: clones of the first and last slides sit at the boundaries
 * so the carousel always scrolls forward seamlessly.
 */
class ImageCarousel extends HTMLElement {
  #current = 0;
  #interval = null;
  #startX = 0;
  #startY = 0;
  #isDragging = false;
  #slideCount = 0;
  #isRepositioning = false;

  connectedCallback() {
    this.track = this.querySelector('.image-carousel__track');
    this.slides = Array.from(this.querySelectorAll('.image-carousel__slide'));
    this.dotsContainer = this.querySelector('.image-carousel__dots');
    this.#slideCount = this.slides.length;

    if (this.#slideCount === 0) return;

    // For infinite loop: clone last slide → prepend, clone first slide → append
    if (this.#slideCount > 1) {
      const firstClone = this.slides[0].cloneNode(true);
      const lastClone = this.slides[this.#slideCount - 1].cloneNode(true);

      firstClone.classList.add('image-carousel__slide--clone');
      lastClone.classList.add('image-carousel__slide--clone');

      // Remove shopify attributes from clones to avoid duplicate IDs
      firstClone.removeAttribute('data-shopify-editor-block');
      lastClone.removeAttribute('data-shopify-editor-block');

      this.track.appendChild(firstClone);
      this.track.insertBefore(lastClone, this.track.firstChild);
    }

    this.#buildDots();
    this.#bindEvents();

    // Start at index 0 which is position 1 in the track (after the prepended clone)
    this.#current = 0;
    this.#setPosition(this.#getOffset(0), false);

    if (this.autoplay) {
      this.#startAutoplay();
    }

    this.addEventListener('mouseenter', () => this.#stopAutoplay());
    this.addEventListener('mouseleave', () => {
      if (this.autoplay) this.#startAutoplay();
    });

    document.addEventListener('visibilitychange', () => {
      if (document.hidden) {
        this.#stopAutoplay();
      } else if (this.autoplay) {
        this.#startAutoplay();
      }
    });
  }

  get autoplay() {
    return this.getAttribute('data-autoplay') === 'true';
  }

  get autoplaySpeed() {
    const speed = parseInt(this.getAttribute('data-speed'), 10);
    return Number.isNaN(speed) ? 5000 : speed * 1000;
  }

  /**
   * Returns the translateX percentage for a given logical slide index.
   * Position 0 in the track is the prepended clone of the last slide.
   * So logical index 0 maps to track position 1.
   */
  #getOffset(logicalIndex) {
    if (this.#slideCount <= 1) return 0;
    const trackPosition = logicalIndex + 1; // +1 because of prepended clone
    return -trackPosition * 100;
  }

  #setPosition(percent, animate = true) {
    if (animate) {
      this.track.style.transition = 'transform 0.45s cubic-bezier(0.4, 0, 0.2, 1)';
    } else {
      this.track.style.transition = 'none';
    }
    this.track.style.transform = `translateX(${percent}%)`;
  }

  // --- Dot navigation ---

  #buildDots() {
    if (!this.dotsContainer) return;
    this.dotsContainer.innerHTML = '';

    for (let i = 0; i < this.#slideCount; i++) {
      const dot = document.createElement('button');
      dot.classList.add('image-carousel__dot');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      if (i === 0) dot.classList.add('image-carousel__dot--active');
      dot.addEventListener('click', () => this.goTo(i));
      this.dotsContainer.appendChild(dot);
    }
  }

  #updateDots() {
    if (!this.dotsContainer) return;
    const dots = this.dotsContainer.querySelectorAll('.image-carousel__dot');
    dots.forEach((dot, i) => {
      dot.classList.toggle('image-carousel__dot--active', i === this.#current);
    });
  }

  // --- Navigation ---

  goTo(index, animate = true) {
    if (this.#isRepositioning) return;
    this.#current = index;
    this.#setPosition(this.#getOffset(index), animate);
    this.#updateDots();
  }

  next() {
    if (this.#isRepositioning) return;

    if (this.#slideCount <= 1) return;

    const nextLogical = this.#current + 1;

    if (nextLogical >= this.#slideCount) {
      // We're going past the last slide → animate to the appended clone (first slide clone)
      const cloneTrackPosition = this.#slideCount + 1;
      this.#setPosition(-cloneTrackPosition * 100, true);
      this.#current = 0;
      this.#updateDots();

      // After animation, snap back to the real first slide (no animation)
      this.#isRepositioning = true;
      setTimeout(() => {
        this.#setPosition(this.#getOffset(0), false);
        this.#isRepositioning = false;
      }, 480);
    } else {
      this.goTo(nextLogical);
    }
  }

  prev() {
    if (this.#isRepositioning) return;

    if (this.#slideCount <= 1) return;

    const prevLogical = this.#current - 1;

    if (prevLogical < 0) {
      // We're going before the first slide → animate to the prepended clone (last slide clone)
      this.#setPosition(0, true); // position 0 = the prepended clone
      this.#current = this.#slideCount - 1;
      this.#updateDots();

      // After animation, snap to the real last slide (no animation)
      this.#isRepositioning = true;
      setTimeout(() => {
        this.#setPosition(this.#getOffset(this.#slideCount - 1), false);
        this.#isRepositioning = false;
      }, 480);
    } else {
      this.goTo(prevLogical);
    }
  }

  // --- Autoplay ---

  #startAutoplay() {
    this.#stopAutoplay();
    this.#interval = setInterval(() => {
      if (!document.hidden) this.next();
    }, this.autoplaySpeed);
  }

  #stopAutoplay() {
    if (this.#interval) {
      clearInterval(this.#interval);
      this.#interval = null;
    }
  }

  // --- Touch / Pointer events ---

  #bindEvents() {
    this.track.addEventListener('touchstart', (e) => this.#onPointerDown(e), { passive: true });
    this.track.addEventListener('touchmove', (e) => this.#onPointerMove(e), { passive: false });
    this.track.addEventListener('touchend', (e) => this.#onPointerUp(e));

    this.track.addEventListener('mousedown', (e) => this.#onPointerDown(e));
    this.track.addEventListener('mousemove', (e) => this.#onPointerMove(e));
    this.track.addEventListener('mouseup', (e) => this.#onPointerUp(e));
    this.track.addEventListener('mouseleave', (e) => {
      if (this.#isDragging) this.#onPointerUp(e);
    });

    this.track.querySelectorAll('img').forEach((img) => {
      img.addEventListener('dragstart', (e) => e.preventDefault());
    });
  }

  #getClientX(e) {
    return e.touches ? e.touches[0].clientX : e.clientX;
  }

  #getClientY(e) {
    return e.touches ? e.touches[0].clientY : e.clientY;
  }

  #onPointerDown(e) {
    if (this.#isRepositioning) return;
    this.#isDragging = true;
    this.#startX = this.#getClientX(e);
    this.#startY = this.#getClientY(e);
    this.track.style.transition = 'none';
    this.track.style.cursor = 'grabbing';
    this.#stopAutoplay();
  }

  #onPointerMove(e) {
    if (!this.#isDragging) return;

    const currentX = this.#getClientX(e);
    const currentY = this.#getClientY(e);
    const diffX = currentX - this.#startX;
    const diffY = currentY - this.#startY;

    if (Math.abs(diffY) > Math.abs(diffX) && Math.abs(diffX) < 10) return;

    if (e.cancelable && Math.abs(diffX) > Math.abs(diffY)) {
      e.preventDefault();
    }

    const trackWidth = this.track.offsetWidth / (this.#slideCount > 1 ? this.#slideCount + 2 : 1);
    const percentMove = (diffX / trackWidth) * 100;
    const baseOffset = this.#getOffset(this.#current);
    this.track.style.transform = `translateX(${baseOffset + percentMove}%)`;
  }

  #onPointerUp(e) {
    if (!this.#isDragging) return;
    this.#isDragging = false;
    this.track.style.cursor = '';

    const endX = e.changedTouches ? e.changedTouches[0].clientX : e.clientX;
    const diff = endX - this.#startX;
    const slideWidth = this.track.offsetWidth / (this.#slideCount > 1 ? this.#slideCount + 2 : 1);
    const threshold = slideWidth * 0.15;

    if (diff < -threshold) {
      this.next();
    } else if (diff > threshold) {
      this.prev();
    } else {
      this.goTo(this.#current);
    }

    if (this.autoplay) {
      this.#startAutoplay();
    }
  }
}

if (!customElements.get('image-carousel-component')) {
  customElements.define('image-carousel-component', ImageCarousel);
}
